import {
    useCallback,
    useState,
    useRef
} from "react";
import {
    useWindowDimensions,
    ActivityIndicator,
    TouchableOpacity,
    ScrollView,
    Image,
    View
} from "react-native";
import type {
    IMultiPhotoUploadModalContentProps,
    SubEventsListNavigationProp,
    SubEventsListRouteProp,
    StatusFilter
} from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitSnackBar,
    NCoreUIKitTheme,
    PageContainer,
    BottomSheet,
    SelectBox,
    Sticker,
    Button,
    Text
} from "ncore-ui-kit";
import {
    useFocusEffect,
    useNavigation,
    useRoute
} from "@react-navigation/native";
import type {
    SubEventItem,
    EventItem
} from "../../../type";
import {
    LocateFixedIcon,
    ArrowLeftIcon,
    CalendarIcon,
    PlusIcon,
    PenIcon,
    XIcon
} from "lucide-react-native";
import moment from "moment";
import RESTServiceContext from "../../../../../services/restAPI/context";

const MultiPhotoUploadModalContent = ({
    subEventID,
    onSuccess,
    eventID,
    onClose
}: IMultiPhotoUploadModalContentProps & { onSuccess?: () => void }) => {
    const {
        radiuses,
        spaces,
        colors
    } = NCoreUIKitTheme.useContext();
    const {
        localize
    } = NCoreUIKitLocalize.useContext();
    const [
        selectedFiles,
        setSelectedFiles
    ] = useState<File[]>([]);
    const [
        isUploading,
        setIsUploading
    ] = useState<boolean>(false);
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            const files = Array.from(e.target.files!);
            const maxFileSize = 5 * 1024 * 1024;
            const hasLargeFile = files.some((file) => file.size > maxFileSize);

            if (hasLargeFile) {
                NCoreUIKitSnackBar.open({
                    title: localize("members-invalid-file-size") || "Dosya boyutu 5 MB'dan küçük olmalıdır."
                });
                return;
            }

            setSelectedFiles((prev) => [
                ...prev,
                ...files
            ]);
        }
        e.target.value = "";
    };

    const handleRemoveFile = (index: number) => {
        setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    };

    const handleStartUpload = async () => {
        if (selectedFiles.length === 0) return;
        setIsUploading(true);
        let hasError = false;

        try {
            for (const file of selectedFiles) {
                const tokenData = await RESTServiceContext.action("getUploadTokenForSubEventPhoto", {
                    eventSubID: subEventID,
                    eventID
                });

                if (tokenData.payload?.token) {
                    const formData = new FormData();
                    formData.append("file", file);
                    const uploadResponse = await fetch(`${process.env.STORAGE_URL}/upload`, {
                        headers: {
                            Authorization: tokenData.payload.token
                        },
                        body: formData,
                        method: "POST"
                    });

                    if (!uploadResponse.ok) {
                        hasError = true;
                    }
                } else {
                    hasError = true;
                }
            }

            if (!hasError) {
                NCoreUIKitSnackBar.open({
                    title: localize("photo-uploaded") || "Fotoğraflar başarıyla yüklendi."
                });
                if (onSuccess) {
                    onSuccess();
                }
            } else {
                NCoreUIKitSnackBar.open({
                    title: localize("photo-upload-error") || "Bazı fotoğraflar yüklenirken hata oluştu."
                });
            }
        } catch (error) {
            console.error(error);
            NCoreUIKitSnackBar.open({
                title: localize("global-error") || "Bir hata oluştu."
            });
        } finally {
            setIsUploading(false);
            setSelectedFiles([]);
            onClose();
        }
    };

    return <View
        style={[
            stylesheet.uploadSheetInnerContent,
            {
                gap: spaces.spacingLg
            }
        ]}
    >
        <Text
            customColor="content.text.high"
            variant="titleMediumSize"
        >
            {localize("events-upload-photo") || "Toplu Fotoğraf Yükle"}
        </Text>
        <TouchableOpacity
            style={[
                stylesheet.modalUploadBox,
                {
                    backgroundColor: colors.content.container.subtle,
                    borderColor: colors.content.border.emphasized,
                    borderRadius: radiuses.md,
                    padding: spaces.spacingXl
                }
            ]}
            onPress={() => fileInputRef.current?.click()}
        >
            <Text
                customColor="content.text.mid"
                variant="bodyMediumSize"
            >
                {localize("click-to-select-photos") || "Fotoğrafları seçmek için tıklayın"}
            </Text>
        </TouchableOpacity>
        <input
            onChange={handleFileSelect}
            style={{
                display: "none"
            }}
            ref={fileInputRef}
            accept="image/*"
            type="file"
            multiple
        />
        {selectedFiles.length > 0 && (
            <ScrollView
                style={stylesheet.modalFilesContainer}
            >
                {selectedFiles.map((file, index) => <View
                    style={[
                        stylesheet.modalFileRow,
                        {
                            backgroundColor: colors.content.container.subtle,
                            borderRadius: radiuses.sm,
                            padding: spaces.spacingMd
                        }
                    ]}
                    key={`${file.name}-${index}`}
                >
                    <View
                        style={[
                            stylesheet.modalFileRowLeft,
                            {
                                gap: spaces.spacingMd
                            }
                        ]}
                    >
                        <Image
                            source={{
                                uri: URL.createObjectURL(file)
                            }}
                            style={[
                                stylesheet.modalFilePreview,
                                {
                                    borderRadius: radiuses.xs
                                }
                            ]}
                        />
                        <Text
                            customColor="content.text.high"
                            variant="bodySmallSize"
                        >
                            {file.name}
                        </Text>
                    </View>
                    <TouchableOpacity
                        onPress={() => handleRemoveFile(index)}
                    >
                        <XIcon
                            color={colors.content.icon.mid}
                            size={16}
                        />
                    </TouchableOpacity>
                </View>)}
            </ScrollView>)}
        <Button
            title={isUploading ? (localize("uploading")) : (localize("start-upload"))}
            isDisabled={selectedFiles.length === 0 || isUploading}
            onPress={handleStartUpload}
            spreadBehaviour="stretch"
            isLoading={isUploading}
            variant="filled"
            type="primary"
        />
    </View>;
};
const CustomStatusChip = ({
    status
}: { status: string | undefined }) => {
    const {
        radiuses,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();
    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    let bgColor = colors.content.container.subtle;
    let textColor = colors.content.text.high;
    let titleText = localize("status");

    if (status === "finished") {
        bgColor = colors.content.container.subtle;
        textColor = colors.content.text.high;
        titleText = localize("events-filter-finished");
    } else if (status === "application") {
        bgColor = colors.project.statusApplication;
        textColor = colors.project.brandWhite;
        titleText = localize("events-filter-application");
    } else if (status === "near") {
        bgColor = colors.project.statusNear;
        textColor = colors.project.brandWhite;
        titleText = localize("events-filter-near");
    } else if (status === "continue") {
        bgColor = colors.project.statusContinue;
        textColor = colors.project.brandWhite;
        titleText = localize("events-filter-continue");
    } else if (status === "announcement") {
        bgColor = colors.project.statusAnnouncement;
        textColor = colors.project.brandWhite;
        titleText = localize("events-filter-announcement");
    }

    return <View
        style={[
            stylesheet.statusChipContainer,
            {
                paddingHorizontal: spaces.spacingMd,
                paddingVertical: spaces.spacingXs,
                borderRadius: radiuses.full,
                backgroundColor: bgColor
            }
        ]}
    >
        <Text
            customColor={textColor}
            variant="bodySmallSize"
        >
            {titleText}
        </Text>
    </View>;
};

const SubEventsList = () => {
    const {
        radiuses,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const navigation = useNavigation<SubEventsListNavigationProp>();
    const route = useRoute<SubEventsListRouteProp>();
    const {
        eventID
    } = route.params || {};

    const [
        loading,
        setLoading
    ] = useState(true);

    const [
        isUploadBottomSheetActive,
        setIsUploadBottomSheetActive
    ] = useState(false);

    const [
        selectedSubEventID,
        setSelectedSubEventID
    ] = useState<string | null>(null);

    const [
        eventData,
        setEventData
    ] = useState<EventItem | null>(null);

    const [
        statusFilter,
        setStatusFilter
    ] = useState<StatusFilter>("all");

    const {
        width
    } = useWindowDimensions();
    const isMobile = width < 768;

    const fetchEventData = useCallback(async () => {
        try {
            setLoading(true);
            const result = await RESTServiceContext.action("getEventForAdmin", {
                eventID: eventID as string
            });
            const evResult = result as unknown as { payload?: Parameters<typeof setEventData>[0] };
            if (evResult.payload) {
                setEventData(evResult.payload);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, [eventID]);

    useFocusEffect(
        useCallback(() => {
            fetchEventData();
        }, [fetchEventData])
    );

    const renderHeader = () => {
        return <View
            style={[
                stylesheet.headerContainer,
                {
                    gap: spaces.spacingMd
                }
            ]}
        >
            <Button
                icon={() => <ArrowLeftIcon
                    color={colors.project.brandWhite}
                    size={24}
                />}
                customBorderColor="secondaryButton"
                onPress={() => navigation.goBack()}
                customColor="secondaryButton"
                spreadBehaviour="free"
                variant="filled"
            />
            <View
                style={{
                    flex: 1
                }}
            >
                <Text
                    customColor={colors.content.text.high}
                    variant="titleLargeSize"
                >
                    {eventData ? eventData.title : localize("events-page-title")}
                </Text>
                {
                    eventData?.description ?
                        <Text
                            customColor={colors.content.text.mid}
                            style={{
                                marginTop: spaces.spacingXs
                            }}
                            variant="bodyMediumSize"
                        >
                            {eventData.description}
                        </Text>
                    :
                        null
                }
            </View>
            <View
                style={{
                    alignItems: "flex-end"
                }}
            >
                <Button
                    onPress={() => {
                        navigation.navigate("SubEventCreate", {
                            eventID: eventID as string
                        });
                    }}
                    icon={({
                        color,
                        size
                    }) => <PlusIcon
                        color={colors.content.icon[color]}
                        size={size}
                    />}
                    title={localize("events-create")}
                    spreadBehaviour="baseline"
                    variant="filled"
                />
            </View>
        </View>;
    };

    const renderFilters = () => {
        return <View
            style={[
                stylesheet.filterSection,
                {
                    flexDirection: isMobile ? "column" : "row",
                    marginTop: spaces.spacingLg,
                    gap: spaces.spacingMd
                }
            ]}
        >
            <View
                style={[
                    stylesheet.filterDropdownContainer,
                    {
                        width: isMobile ? "100%" : 220
                    }
                ]}
            >
                <SelectBox
                    data={[
                        {
                            title: localize("events-filter-all"),
                            value: "all"
                        },
                        {
                            title: localize("events-filter-announcement"),
                            value: "announcement"
                        },
                        {
                            title: localize("events-filter-application"),
                            value: "application"
                        },
                        {
                            title: localize("events-filter-near"),
                            value: "near"
                        },
                        {
                            title: localize("events-filter-continue"),
                            value: "continue"
                        },
                        {
                            title: localize("events-filter-finished"),
                            value: "finished"
                        }
                    ]}
                    onChange={selectedItems => {
                        const item = selectedItems[0];
                        if (item) {
                            setStatusFilter(item.__key as StatusFilter);
                        }
                    }}
                    placeholder={localize("events-filter-all")}
                    customTheme={{
                        themeKey: "events_filter" as never
                    }}
                    titleExtractor={item => item.title}
                    keyExtractor={item => item.value}
                    title={localize("status")}
                    spreadBehaviour="stretch"
                />
            </View>
        </View>;
    };

    const renderSubEventCard = (subEvent: SubEventItem, index: number, isPast: boolean) => {
        const subEventTitle = eventData?.title || localize("events-title-input");
        const locationText = subEvent.eventLocation || "";

        return <TouchableOpacity
            key={`subevent-${isPast ? "past" : "active"}-${index}`}
            onPress={() => {
                navigation.navigate("SubEventDetail", {
                    subEventID: subEvent.eventSubID as string,
                    eventID: eventID as string
                });
            }}
            style={stylesheet.cardWrapper}
        >
            <View
                style={[
                    stylesheet.cardContainer,
                    {
                        alignItems: isMobile ? "flex-start" : "center",
                        backgroundColor: colors.project.brandWhite,
                        flexDirection: isMobile ? "column" : "row",
                        paddingHorizontal: spaces.spacingXl,
                        paddingVertical: spaces.spacingMd,
                        justifyContent: "space-between",
                        borderRadius: radiuses.md,
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <View
                    style={{
                        gap: spaces.spacingMd,
                        flex: 1
                    }}
                >
                    <View
                        style={[
                            stylesheet.dateContainer,
                            {
                                gap: spaces.spacingMd,
                                flexDirection: "row",
                                alignItems: "center"
                            }
                        ]}
                    >
                        <Text
                            customColor={colors.content.text.high}
                            style={stylesheet.cardTitle}
                            variant="titleMediumSize"
                        >
                            {subEventTitle}
                        </Text>
                        <Sticker
                            title={moment(subEvent.startDate).format("YYYY")}
                            type="warning"
                            size="small"
                        />
                    </View>
                    <View
                        style={[
                            stylesheet.locationContainer,
                            {
                                gap: spaces.spacingLg,
                                flexDirection: "row",
                                alignItems: "center"
                            }
                        ]}
                    >
                        <View
                            style={{
                                flexDirection: "row",
                                alignItems: "center",
                                gap: spaces.spacingXs
                            }}
                        >
                            <CalendarIcon
                                color={colors.content.icon.mid}
                                size={14}
                            />
                            <Text
                                customColor={colors.content.text.mid}
                                variant="bodyMediumSize"
                            >
                                {moment(subEvent.startDate).format("DD MMM HH:mm")} - {moment(subEvent.endDate).format("HH:mm")}
                            </Text>
                        </View>
                        {
                            locationText ?
                                <View
                                    style={{
                                        flexDirection: "row",
                                        alignItems: "center",
                                        gap: spaces.spacingXs
                                    }}
                                >
                                    <LocateFixedIcon
                                        color={colors.content.icon.mid}
                                        size={14}
                                    />
                                    <Text
                                        customColor={colors.content.text.mid}
                                        variant="bodyMediumSize"
                                    >
                                        {locationText}
                                    </Text>
                                </View>
                            :
                                null
                        }
                        <CustomStatusChip
                            status={isPast ? "finished" : (subEvent.status || "continue")}
                        />
                    </View>
                </View>
                <View
                    style={[
                        stylesheet.actionButtonsContainer,
                        {
                            width: isMobile ? "100%" : "auto",
                            gap: spaces.spacingMd,
                            flexDirection: "row",
                            alignItems: "center"
                        }
                    ]}
                >
                    <View
                        style={{
                            gap: spaces.spacingMd,
                            flexDirection: "row",
                            alignItems: "center"
                        }}
                    >
                        {
                            isPast ?
                                <Button
                                    onPress={() => {
                                        setSelectedSubEventID(subEvent.eventSubID as string);
                                        setIsUploadBottomSheetActive(true);
                                    }}
                                    title={localize("sub-event-detail-upload-photo")}
                                    customTextColor={colors.project.brandWhite}
                                    customBorderColor="gold"
                                    spreadBehaviour="free"
                                    customColor="gold"
                                    variant="filled"
                                />
                            :
                                null
                        }
                        <Button
                            onPress={() => {
                                navigation.navigate("SubEventEdit", {
                                    subEventID: subEvent.eventSubID as string,
                                    eventID: eventID as string
                                });
                            }}
                            title={localize("sub-event-detail-edit")}
                            icon={({
                                color,
                                size
                            }) => <PenIcon
                                color={colors.content.icon[color]}
                                size={size}
                            />}
                            customBorderColor="secondaryButton"
                            customColor="secondaryButton"
                            spreadBehaviour="free"
                            variant="filled"
                        />
                    </View>
                </View>
            </View>
        </TouchableOpacity>;
    };

    const renderContent = () => {
        if (loading) {
            return <View
                style={[
                    stylesheet.emptyContainer,
                    {
                        padding: spaces.spacingXl
                    }
                ]}
            >
                <ActivityIndicator
                    color={colors.project.brandPrimary}
                    size="large"
                />
            </View>;
        }

        const activeSubEvents = eventData?.activeEvents || [];
        const pastSubEvents = eventData?.pastEvents || [];
        const allSubEvents = [
            ...activeSubEvents.map((e) => ({
                ...e,
                isPast: false
            })),
            ...pastSubEvents.map((e) => ({
                ...e,
                isPast: true
            }))
        ];

        const filteredSubEvents = allSubEvents.filter(subEvent => {
            if (statusFilter === "all") {
                return true;
            }
            return subEvent.status === statusFilter;
        });

        if (filteredSubEvents.length === 0) {
            return <View
                style={[
                    stylesheet.emptyContainer,
                    {
                        padding: spaces.spacingXl
                    }
                ]}
            >
                <Text
                    customColor="content.text.mid"
                    style={stylesheet.emptyText}
                    variant="bodyLargeSize"
                >
                    {localize("events-empty-list")}
                </Text>
            </View>;
        }

        return <View
            style={[
                stylesheet.gridContainer,
                {
                    marginTop: spaces.spacingMd,
                    gap: spaces.spacingMd
                }
            ]}
        >
            {filteredSubEvents.map((subEvent, index) => renderSubEventCard(subEvent, index, subEvent.isPast))}
        </View>;
    };

    return <PageContainer
        isScrollable={true}
    >
        <View
            style={[
                stylesheet.container,
                {
                    padding: spaces.spacingXl
                }
            ]}
        >
            {renderHeader()}
            {renderFilters()}
            {renderContent()}
        </View>
        <BottomSheet
            onClose={() => setIsUploadBottomSheetActive(false)}
            isActive={isUploadBottomSheetActive}
            portalName="modal-system"
        >
            <View
                style={[
                    stylesheet.uploadSheetContent,
                    {
                        backgroundColor: colors.content.container.default,
                        borderRadius: radiuses.lg,
                        padding: spaces.spacingXl
                    }
                ]}
            >
                <MultiPhotoUploadModalContent
                    onClose={() => setIsUploadBottomSheetActive(false)}
                    subEventID={selectedSubEventID || ""}
                    onSuccess={() => fetchEventData()}
                    eventID={eventID as string}
                />
            </View>
        </BottomSheet>
    </PageContainer>;
};

export default SubEventsList;
